import React, { useState } from 'react';
import { Eye, EyeOff, Plus, Trash2 } from 'lucide-react';

const DependentFields = ({ formData, setFormData, required = false }) => {
    const [visibleSSNs, setVisibleSSNs] = useState({});
    const maxDependents = 10; // Based on dep1 through dep10 in the data structure

    // Initialize dependents from existing data with unique IDs
    const [dependents, setDependents] = useState(() => {
        const deps = [];
        for (let i = 1; i <= maxDependents; i++) {
            const nameKey = `dep${i}_name`;
            if (formData[nameKey]) {
                deps.push({
                              id: `dep-${Date.now()}-${i}`, // Add unique ID
                              name: formData[`dep${i}_name`] || '',
                              relationship: formData[`dep${i}_rel`] || '',
                              dob: formData[`dep${i}_dob`] || '',
                              ssn: formData[`dep${i}_ssn`] || ''
                          });
            }
        }
        return deps.length ? deps : [{
            id: `dep-${Date.now()}-1`,
            name: '',
            relationship: '',
            dob: '',
            ssn: ''
        }];
    });

    const toggleSSNVisibility = (id) => {
        setVisibleSSNs(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleDependentChange = (id, field, value) => {
        const newDependents = dependents.map(dep =>
                                                 dep.id === id ? { ...dep, [field]: value } : dep
        );
        setDependents(newDependents);

        // Update parent form data
        const updatedFormData = { ...formData };
        for (let i = 1; i <= maxDependents; i++) {
            if (i - 1 < newDependents.length) {
                updatedFormData[`dep${i}_name`] = newDependents[i - 1].name;
                updatedFormData[`dep${i}_rel`] = newDependents[i - 1].relationship;
                updatedFormData[`dep${i}_dob`] = newDependents[i - 1].dob;
                updatedFormData[`dep${i}_ssn`] = newDependents[i - 1].ssn;
            } else {
                updatedFormData[`dep${i}_name`] = '';
                updatedFormData[`dep${i}_rel`] = '';
                updatedFormData[`dep${i}_dob`] = '';
                updatedFormData[`dep${i}_ssn`] = '';
            }
        }
        setFormData(updatedFormData);
    };

    const addDependent = () => {
        if (dependents.length < maxDependents) {
            setDependents([
                              ...dependents,
                              {
                                  id: `dep-${Date.now()}-${dependents.length + 1}`,
                                  name: '',
                                  relationship: '',
                                  dob: '',
                                  ssn: ''
                              }
                          ]);
        }
    };

    const removeDependent = (idToRemove) => {
        if (dependents.length > 1) {
            const newDependents = dependents.filter(dep => dep.id !== idToRemove);
            setDependents(newDependents);

            // Update parent form data
            const updatedFormData = { ...formData };
            for (let i = 1; i <= maxDependents; i++) {
                if (i - 1 < newDependents.length) {
                    updatedFormData[`dep${i}_name`] = newDependents[i - 1].name;
                    updatedFormData[`dep${i}_rel`] = newDependents[i - 1].relationship;
                    updatedFormData[`dep${i}_dob`] = newDependents[i - 1].dob;
                    updatedFormData[`dep${i}_ssn`] = newDependents[i - 1].ssn;
                } else {
                    updatedFormData[`dep${i}_name`] = '';
                    updatedFormData[`dep${i}_rel`] = '';
                    updatedFormData[`dep${i}_dob`] = '';
                    updatedFormData[`dep${i}_ssn`] = '';
                }
            }
            setFormData(updatedFormData);
        }
    };

    return (
        <div className="space-y-4">
            {dependents.map((dependent) => (
                <div key={dependent.id} className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={dependent.name}
                                required={required}
                                onChange={(e) => handleDependentChange(dependent.id, 'name', e.target.value)}
                                className="w-full rounded-lg border px-4 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"

                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                Relationship <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                required={required}
                                value={dependent.relationship}
                                onChange={(e) => handleDependentChange(dependent.id, 'relationship', e.target.value)}
                                className="w-full rounded-lg border px-4 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"

                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                Date of Birth <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={dependent.dob}
                                required={required}

                                onChange={(e) => handleDependentChange(dependent.id, 'dob', e.target.value)}
                                className="w-full rounded-lg border px-4 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"

                            />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                SSN <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type={visibleSSNs[dependent.id] ? 'text' : 'password'}
                                    value={dependent.ssn}
                                    required={required}
                                    onChange={(e) => handleDependentChange(dependent.id, 'ssn', e.target.value)}
                                    className="w-full rounded-lg border px-4 py-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 pr-10"

                                    minLength={9}
                                    maxLength={11}
                                />
                                <button
                                    type="button"
                                    onClick={() => toggleSSNVisibility(dependent.id)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                >
                                    {visibleSSNs[dependent.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {dependents.length > 1 && (
                        <button
                            type="button"
                            onClick={() => removeDependent(dependent.id)}
                            className="mt-4 flex items-center text-red-600 hover:text-red-700 text-sm"
                        >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove Dependent
                        </button>
                    )}
                </div>
            ))}

            {dependents.length < maxDependents && (
                <button
                    type="button"
                    onClick={addDependent}
                    className="mt-4 flex items-center text-blue-600 hover:text-blue-700 text-sm"
                >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Another Dependent
                </button>
            )}
        </div>
    );
};

export default DependentFields;
